// hooks/useEventHandlers.js - VERSÃO CORRIGIDA PARA USAR SEU HOOK
import { nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export function useEventHandlers(
  tabManagement,
  dataFetching,
  formValidation,
  computedProps,
  selectedTable,
  showModal,
  setSelectedTable,
  props
) {
  const store = useStore();
  const router = useRouter();

  const validateCurrentTab = async () => {
    const currentTab = tabManagement.activeTab.value;

    const isPlaceholdersValid = {
      value: tabManagement.tabValidation.value.placeholders,
    };

    try {
      const isValid = await formValidation.validateCurrentTab(
        currentTab,
        isPlaceholdersValid
      );

      // Atualizar a validação no hook de gerenciamento de tabs
      tabManagement.updateValidation(currentTab, isValid);

      return isValid;
    } catch (error) {
      console.error('❌ [validateCurrentTab] Erro durante validação:', error);
      return false;
    }
  };

  const onTabChange = async tabId => {
    await tabManagement.onTabChange(
      tabId,
      validateCurrentTab,
      formValidation.filtersRef
    );
  };

  const nextTab = async () => {
    await tabManagement.nextTab(validateCurrentTab);
  };

  // 🔧 CORREÇÃO: Handler de mudança de tabela que salva dados usando seu hook
  const onTableChanged = async tableName => {
    // Atualizar estado local
    selectedTable.value = tableName;

    if (tableName) {
      try {
        // Resetar validação temporariamente
        tabManagement.updateValidation('table', false);

        // Carregar dados da tabela
        await Promise.all([
          dataFetching.fetchTableColumns(tableName),
          setSelectedTable(tableName),
        ]);

        // 🔧 CORREÇÃO: Salvar dados da tabela usando os métodos disponíveis no seu hook
        if (formValidation.selectedPublicRef.value) {
          // Coletar dados atuais do componente
          const tableData = {
            selectedTable: tableName,
            selectedPhoneColumn:
              formValidation.selectedPublicRef.value.selectedPhoneColumn || '',
            selectedContactColumn:
              formValidation.selectedPublicRef.value.selectedContactColumn ||
              '',
          };

          // Salvar no Vuex usando função do hook (se disponível)
          if (formValidation.saveToVuex) {
            await formValidation.saveToVuex(tableData);
          } else {
            // Fallback: salvar diretamente no store
            const currentData = store.getters['campaigns/getCampaignData'];
            await store.dispatch('campaigns/saveCampaignData', {
              ...currentData,
              ...tableData,
            });
          }
        }

        // Limpar filtros relacionados
        await formValidation.onFiltersChange([]);

        if (formValidation.filtersRef.value?.resetFilters) {
          formValidation.filtersRef.value.resetFilters();
        }

        await nextTick();
      } catch (error) {
        console.error('❌ [onTableChanged] Erro ao carregar dados:', error);

        // Limpar estado em caso de erro
        if (dataFetching.phoneColumns) dataFetching.phoneColumns.value = [];
        if (dataFetching.nameColumns) dataFetching.nameColumns.value = [];
        if (dataFetching.allColumns) dataFetching.allColumns.value = [];

        tabManagement.updateValidation('table', false);
      }
    } else {
      // Limpar todos os dados relacionados
      if (dataFetching.phoneColumns) dataFetching.phoneColumns.value = [];
      if (dataFetching.nameColumns) dataFetching.nameColumns.value = [];
      if (dataFetching.allColumns) dataFetching.allColumns.value = [];
      selectedTable.value = '';

      // Limpar filtros
      await formValidation.onFiltersChange([]);

      if (formValidation.filtersRef.value?.resetFilters) {
        formValidation.filtersRef.value.resetFilters();
      }

      await setSelectedTable(null);
      tabManagement.updateValidation('table', false);
    }
  };

  // 🔧 CORREÇÃO: Handlers de placeholders que usam os métodos do seu hook
  const onPlaceholdersSubmit = async selections => {
    if (selections && typeof selections === 'object') {
      // Usar o método do seu hook
      await formValidation.onPlaceholdersSubmit(selections);
    }
  };

  const onPlaceholdersChange = async selections => {
    if (selections && typeof selections === 'object') {
      // 🔧 CORREÇÃO: Usar o método do seu hook com o parâmetro correto
      const isPlaceholdersValid = { value: false }; // Objeto com propriedade value

      await formValidation.onPlaceholdersChange(
        selections,
        isPlaceholdersValid
      );

      // Atualizar validação no hook de tabs
      tabManagement.updateValidation('placeholders', isPlaceholdersValid.value);
    }
  };

  // Handler de filtros
  const onFiltersChange = async newFilters => {
    // Usar o método do seu hook
    await formValidation.onFiltersChange(newFilters);

    // Filtros são sempre válidos (opcionais)
    tabManagement.updateValidation('filters', true);
  };

  // Modal handlers
  const onModalClosed = () => {
    showModal.value = false;
  };

  const fetchTablePreview = async tableName => {
    showModal.value = true;
    const safeFilters = Array.isArray(formValidation.filters.value)
      ? formValidation.filters.value
      : [];

    await dataFetching.fetchTablePreview(tableName, safeFilters);
  };

  // 🔧 CORREÇÃO: Form submission que usa dados do Vuex
  const handleSubmit = async () => {
    // Validar tab atual primeiro
    const isCurrentTabValid = await validateCurrentTab();
    const canSubmit = tabManagement.canSubmit.value;

    if (!isCurrentTabValid || !canSubmit) {
      return;
    }

    // Usar dados diretamente do Vuex (que é a fonte da verdade)
    const currentData = store.getters['campaigns/getCampaignData'];

    const formData = {
      selectedTable: currentData.selectedTable || '',
      selectedPhoneColumn: currentData.selectedPhoneColumn || '',
      selectedContactColumn: currentData.selectedContactColumn || '',
      filters: JSON.parse(JSON.stringify(currentData.filters || [])),
      placeholders: JSON.parse(JSON.stringify(currentData.placeholders || {})),
      template: JSON.parse(
        JSON.stringify(
          currentData.template || computedProps.selectedTemplate.value || {}
        )
      ),
    };

    try {
      // Salvar dados finais (garantindo que tudo está atualizado)
      await store.dispatch('campaigns/saveCampaignData', formData);

      // Determine route based on context (whatsapp_app vs whatsapp_oficial)
      const isAppCtx = router.currentRoute.value.path.includes('/new-app/');
      const scheduleRoute = isAppCtx
        ? 'campaigns_whatsapp_app_schedule_step'
        : 'campaigns_whatsapp_official_schedule_step';

      router.push({
        name: scheduleRoute,
        params: { accountId: store.state.auth.accountId },
        query: {
          templateName: props.templateName,
          ...(isAppCtx && { sourceType: 'database' }),
        },
      });
    } catch (error) {
      console.error(
        '❌ [handleSubmit] Erro ao salvar dados da campanha:',
        error
      );
    }
  };

  return {
    validateCurrentTab,
    onTabChange,
    nextTab,
    onTableChanged,
    onPlaceholdersSubmit,
    onPlaceholdersChange,
    onFiltersChange,
    onModalClosed,
    fetchTablePreview,
    handleSubmit,
  };
}
